import { useCallback, useEffect, useRef, useState } from 'react';

import { DomOffsetMatch, findDomOffsetMatches } from '../helpers/domSearchMatches';
import { clearSearchHighlightOverlay, paintSearchHighlightOverlay, scrollRangeIntoScrollContainer } from '../helpers/searchHighlightOverlay';

interface UseWordViewerSearchArgs {
    wordContentRef: React.RefObject<HTMLDivElement | null>;
    searchOverlayRef: React.RefObject<HTMLDivElement | null>;
    scrollRef: React.RefObject<HTMLDivElement | null>;
    docReady: boolean;
}

export function useWordViewerSearch({
    wordContentRef,
    searchOverlayRef,
    scrollRef,
    docReady,
}: UseWordViewerSearchArgs) {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [matches, setMatches] = useState<DomOffsetMatch[]>([]);
    const [matchIndex, setMatchIndex] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQuery(query), 280);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const root = wordContentRef.current;
        const needle = debouncedQuery.trim();
        if (!docReady || !root || !needle) {
            setMatches([]);
            setMatchIndex(0);
            clearSearchHighlightOverlay(searchOverlayRef.current);
            return;
        }

        setMatches(findDomOffsetMatches(root, needle));
        setMatchIndex(0);
    }, [debouncedQuery, docReady, searchOverlayRef, wordContentRef]);

    const activeMatch = matches.length > 0 ? matches[matchIndex] : null;
    const needle = debouncedQuery.trim();

    const repaint = useCallback(() => {
        const root = wordContentRef.current;
        const overlay = searchOverlayRef.current;
        if (!root || !overlay) return;

        if (!needle || matches.length === 0) {
            clearSearchHighlightOverlay(overlay);
            return;
        }

        paintSearchHighlightOverlay(
            root,
            overlay,
            matches.map((m, i) => ({
                startOffset: m.startOffset,
                endOffset: m.endOffset,
                active: i === matchIndex,
            })),
        );

        if (activeMatch) {
            scrollRangeIntoScrollContainer(
                scrollRef.current,
                root,
                activeMatch.startOffset,
                activeMatch.endOffset,
            );
        }
    }, [activeMatch, matchIndex, matches, needle, scrollRef, searchOverlayRef, wordContentRef]);

    useEffect(() => {
        const t = window.setTimeout(repaint, 40);
        return () => clearTimeout(t);
    }, [repaint]);

    const goToMatch = useCallback((index: number) => {
        if (matches.length === 0) return;
        setMatchIndex(((index % matches.length) + matches.length) % matches.length);
    }, [matches.length]);

    const goNext = useCallback(() => goToMatch(matchIndex + 1), [goToMatch, matchIndex]);
    const goPrev = useCallback(() => goToMatch(matchIndex - 1), [goToMatch, matchIndex]);

    return {
        query,
        setQuery,
        searching: false,
        matchCount: matches.length,
        matchIndex,
        matchLabel: matches.length > 0 ? `${matchIndex + 1} / ${matches.length}` : '0 / 0',
        goNext,
        goPrev,
        searchInputRef,
    };
}
